// Device-to-device sync over a WebRTC data channel, with no server. Ported from HRT Diary.
//
// Pairing: one device makes an offer, the other an answer, and each is carried to the other
// device as a QR code or a pasted code (codes.ts). No STUN or TURN servers are used, so both
// devices must be on the same local network.
//
// Protocol (both sides run the same steps):
//   hello   → every tree, the deleted trees, the synced settings, and the media files this device has; each side then
//             works out what would change on both devices and shows it
//   confirm / cancel → the person's answer, given on either device, applies to both
//   want    → media files this device needs after merging
//   file    → one file's bytes (answering want); files-done after the last one
//   applied → this device has saved the merge; when both have, the sync is finished

import { NATIVE_VERSION } from '../io/native';
import * as db from '../model/db';
import { describePlan, describeSettings, filesUsed, mergeSettings, planSync, syncedSettings, type SidePreview, type SyncedSettings, type TreesPlan } from '../model/sync';
import type { Tree } from '../model/types';
import { findTheme } from '../ui/themes';
import { adoptSyncedSettings, allTrees, applySyncPlan, deletedTrees, getState, requestPersistence } from '../ui/store';
import { buildSdp, decodeSignal, deviceName, encodeSignal, MDNS, pairSecret, parseSdp, reconnectCredentials, toHex, type Signal } from './codes';

const APP = 'relationships';
const CHUNK = 16 * 1024;
const DAY = 86400000;

const enc = new TextEncoder();
const dec = new TextDecoder();

// ---------------------------------------------------------------- this device

export async function deviceId(): Promise<string> {
  let id = await db.getKv<string>('deviceId');
  if (!id) {
    id = crypto.randomUUID ? crypto.randomUUID() : uuidFallback();
    await db.setKv('deviceId', id);
  }
  return id;
}

function uuidFallback(): string {
  const h = toHex(crypto.getRandomValues(new Uint8Array(16)));
  return `${h.slice(0, 8)}-${h.slice(8, 12)}-4${h.slice(13, 16)}-8${h.slice(17, 20)}-${h.slice(20, 32)}`;
}

// ---------------------------------------------------------------- remembered devices
// After a first pairing, each device keeps the other's certificate fingerprint and a shared
// secret (kv 'syncPairs'), and uses one stored certificate for every sync (kv 'syncCert'). The
// next time, the first device can build the other's reply itself: the reply's certificate is
// known, and its ICE username and password are derived from the secret and the offer. So one
// scan is enough, and the fingerprint check still means only the remembered device can connect.

export interface Pair {
  name: string;
  /** The other device's certificate fingerprint. */
  fp: string;
  /** This device's, when the pair was made. */
  myFp: string;
  secret: string;
  /** Whether that device can scan this one's one-scan code (see canEditIceCredentials). */
  canAnswer: boolean;
}

/** When this device last synced with each other one. */
export interface Peer {
  name: string;
  at: string;
}

export async function getPairs(): Promise<Record<string, Pair>> {
  return (await db.getKv<Record<string, Pair>>('syncPairs')) ?? {};
}

export async function getPeers(): Promise<Record<string, Peer>> {
  return (await db.getKv<Record<string, Peer>>('syncPeers')) ?? {};
}

export async function forgetDevice(id: string) {
  const pairs = await getPairs();
  delete pairs[id];
  await db.setKv('syncPairs', pairs);
  const peers = await getPeers();
  delete peers[id];
  await db.setKv('syncPeers', peers);
}

/**
 * This device's stored certificate, replaced when it's close to expiring (browsers cap the
 * lifetime at a year). keep: use the stored one while it's still valid at all, as answering a
 * reconnect needs the certificate the other device remembers.
 */
async function getCert({ keep = false } = {}): Promise<{ cert: RTCCertificate; persisted: boolean }> {
  let cert: RTCCertificate | undefined;
  try {
    cert = await db.getKv<RTCCertificate>('syncCert');
  } catch {
    /* not stored */
  }
  if (cert && typeof cert.expires === 'number' && cert.expires > Date.now() + (keep ? 60000 : 10 * DAY)) return { cert, persisted: true };
  cert = await RTCPeerConnection.generateCertificate({ name: 'ECDSA', namedCurve: 'P-256', expires: 365 * DAY } as AlgorithmIdentifier);
  try {
    await db.setKv('syncCert', cert);
    return { cert, persisted: true };
  } catch {
    // Some browsers can't store certificates; this device then always pairs with two codes.
    return { cert, persisted: false };
  }
}

/**
 * Whether this browser keeps ICE credentials edited into its own answer, which answering a
 * one-scan reconnect needs (Chrome and Safari do; Firefox has ignored or rejected such edits).
 * Checked once with two throwaway connections.
 */
let editCheck: Promise<boolean> | null = null;
export function canEditIceCredentials(): Promise<boolean> {
  editCheck ||= (async () => {
    const a = new RTCPeerConnection({ iceServers: [] });
    const b = new RTCPeerConnection({ iceServers: [] });
    try {
      a.createDataChannel('check');
      await a.setLocalDescription(await a.createOffer());
      await b.setRemoteDescription(a.localDescription!);
      const answer = await b.createAnswer();
      const ufrag = 'ab12cd34';
      const sdp = answer.sdp!.replace(/^a=ice-ufrag:.*/gm, `a=ice-ufrag:${ufrag}`).replace(/^a=ice-pwd:.*/gm, `a=ice-pwd:${'0123456789abcdef'.repeat(2)}`);
      await b.setLocalDescription({ type: 'answer', sdp });
      return new RegExp(`^a=ice-ufrag:${ufrag}\\b`, 'm').test(b.localDescription!.sdp);
    } catch {
      return false;
    } finally {
      a.close();
      b.close();
    }
  })();
  return editCheck;
}

function certFingerprint(cert: RTCCertificate): string | null {
  const f = cert.getFingerprints().find((x) => x.algorithm === 'sha-256');
  return f?.value ? f.value.toUpperCase() : null;
}

function waitForIce(pc: RTCPeerConnection, ms = 3000): Promise<void> {
  if (pc.iceGatheringState === 'complete') return Promise.resolve();
  return new Promise((resolve) => {
    const t = setTimeout(done, ms);
    function done() {
      clearTimeout(t);
      pc.removeEventListener('icegatheringstatechange', check);
      resolve();
    }
    function check() {
      if (pc.iceGatheringState === 'complete') done();
    }
    pc.addEventListener('icegatheringstatechange', check);
  });
}

// ---------------------------------------------------------------- one sync

export interface Preview {
  here: SidePreview;
  there: SidePreview;
  /** The other device's name. */
  device: string;
}

export interface Summary {
  added: number;
  changed: number;
  removed: number;
  files: number;
  /** Lines from describeSettings for this device. */
  settings: string[];
  device: string;
}

export interface SyncCallbacks {
  onProgress: (text: string) => void;
  onDone: (s: Summary) => void;
  onError: (e: SyncError) => void;
  onConnected: () => void;
  onPreview: (p: Preview) => void;
  onCanceled: () => void;
  onConfirmed: () => void;
}

export type SyncError = Error & { details?: string; fromPeer?: boolean };

interface Hello {
  t: 'hello';
  app: string;
  format: number;
  deviceId: string;
  device: string;
  trees: Tree[];
  deletedTrees: Record<string, string>;
  /** Missing from a device on a version from before settings synced. */
  settings?: SyncedSettings;
  fileKeys: string[];
  /** The certificate this device will use next time; null if it can't keep one. */
  fp: string | null;
  canAnswer: boolean;
  nonce: string;
}

type Message =
  | Hello
  | { t: 'confirm' | 'cancel' | 'files-done' | 'applied' }
  | { t: 'want'; keys: string[] }
  | { t: 'file'; key: string; mime: string };

const noop = () => {};
const themeName = (id: string) => findTheme(id).name;

/**
 * One sync attempt. Usage:
 *   offerer:  const s = await SyncSession.create(cb); bytes = await s.createOffer(); … await s.acceptAnswer(bytes)
 *   answerer: const s = await SyncSession.create(cb); bytes = await s.acceptOffer(offerBytes)
 * Then the channel opens and the sync runs, reporting through the callbacks.
 */
export class SyncSession {
  /** keepCert: this session answers a reconnect, so it must use the remembered certificate. */
  static async create(callbacks: Partial<SyncCallbacks>, { keepCert = false } = {}): Promise<SyncSession> {
    const { cert, persisted } = await getCert({ keep: keepCert });
    return new SyncSession(callbacks, cert, persisted);
  }

  cb: SyncCallbacks;
  readonly pc: RTCPeerConnection;
  private ch: RTCDataChannel;
  private cert: RTCCertificate;
  private persisted: boolean;
  /** A reconnect: the remembered device this must be. */
  private expectPeer: string | null = null;
  private t0 = Date.now();
  private events: string[] = [];
  private role = 'new';
  private isOfferer = false;
  private holdTimer: ReturnType<typeof setTimeout> | undefined;
  private queue: Promise<void> = Promise.resolve();
  private incoming: { size: number; parts: Uint8Array[]; got: number } | null = null;
  opened = false;
  finished = false;
  confirmed = false;
  /** Whether the camera was on while this device's code was made (for "Technical details"). */
  usedCamera = false;
  private remote: Hello | null = null;
  private local: { trees: Tree[]; deletedTrees: Record<string, string>; settings: SyncedSettings } | null = null;
  private wanted = new Set<string>();
  private fileTotal = 0;
  private received = 0;
  private remoteFilesDone = false;
  private applied = false;
  private remoteApplied = false;
  private myFp: string | null = null;
  private nonce = '';
  private summary: Summary | null = null;

  constructor(callbacks: Partial<SyncCallbacks>, cert: RTCCertificate, persisted: boolean) {
    this.cb = { onProgress: noop, onDone: noop, onError: noop, onConnected: noop, onPreview: noop, onCanceled: noop, onConfirmed: noop, ...callbacks };
    this.cert = cert;
    this.persisted = persisted;
    this.pc = new RTCPeerConnection({ iceServers: [], certificates: [cert] });
    this.ch = this.pc.createDataChannel('sync', { negotiated: true, id: 0, ordered: true });
    this.ch.binaryType = 'arraybuffer';
    this.ch.bufferedAmountLowThreshold = 256 * 1024;
    this.ch.onopen = () => {
      this.opened = true;
      clearTimeout(this.holdTimer);
      this.note('channel open');
      // Handle the other device's messages only after this one's hello has gone out: building it
      // takes a moment, and replying to the other hello meanwhile sent "confirm" or "want" ahead
      // of it, so the other device synced with no hello.
      this.queue = this.start().catch((e) => this.fail(e));
    };
    this.ch.onmessage = (e) => this.onFrame(e.data);
    this.ch.onclose = () => {
      this.note('channel closed');
      if (this.finished) return;
      this.fail(
        new Error(
          this.opened
            ? 'The other device disconnected before the sync finished. Keep the app open, with the screen on, on both devices until it finishes.'
            : 'Couldn’t connect. Make sure both devices are on the same Wi-Fi network with this app open, then try again.',
        ),
      );
    };
    this.pc.oniceconnectionstatechange = () => this.note(`ICE ${this.pc.iceConnectionState}`);
    // Only the device that made the offer gives up on "failed": the other one starts its checks as
    // soon as it shows its reply, and they can time out while the person is still scanning it,
    // yet still connect once the first device has the reply.
    this.pc.onconnectionstatechange = () => {
      this.note(`connection ${this.pc.connectionState}`);
      if (this.pc.connectionState === 'failed' && this.isOfferer) this.fail(new Error('Couldn’t connect. Make sure both devices are on the same Wi-Fi network, then try again.'));
    };
  }

  private async localSignal<T extends 'offer' | 'answer' | 'reconnect'>(type: T) {
    await waitForIce(this.pc);
    let parts = parseSdp(this.pc.localDescription!.sdp);
    // Registering the private .local name occasionally fails on the first try (seen in WebKit and
    // Chrome); gathering again usually works. Only an offer can restart.
    if (!parts.candidates.length && type !== 'answer') {
      await this.pc.setLocalDescription(await this.pc.createOffer({ iceRestart: true }));
      await waitForIce(this.pc);
      parts = parseSdp(this.pc.localDescription!.sdp);
    }
    if (!parts.candidates.length) throw new Error('This browser didn’t share a local network address. Check that you’re connected to Wi-Fi, then try again.');
    return parts;
  }

  private note(text: string) {
    this.events.push(`${((Date.now() - this.t0) / 1000).toFixed(1)}s ${text}`);
  }

  /**
   * What happened, for "Technical details": the timeline, then this side's and the other side's
   * addresses and every connection check between them (from getStats).
   */
  async details(): Promise<string> {
    const pc = this.pc;
    const lines = [
      `role: ${this.role}; browser: ${deviceName()}${this.usedCamera ? '; camera on while the code was made' : ''}`,
      `now: connection ${pc.connectionState}, ICE ${pc.iceConnectionState}, signaling ${pc.signalingState}, channel ${this.ch.readyState}`,
      ...this.events,
    ];
    try {
      const st = await pc.getStats();
      const byId = new Map<string, any>();
      st.forEach((r) => byId.set(r.id, r));
      const addr = (r: any) => (r ? `${r.candidateType || '?'} ${r.address || r.ip || '?'}:${r.port} ${r.protocol || ''}`.trim() : '?');
      const of = (type: string) => [...byId.values()].filter((r) => r.type === type);
      // Whether each side offered a private .local name or a real IP (statistics hide both).
      const kinds = (desc: RTCSessionDescription | null) =>
        desc ? parseSdp(desc.sdp).candidates.map((c) => (MDNS.test(c.addr) ? 'private .local name' : `IP ${c.addr}`)).join(', ') || 'none' : 'none';
      lines.push(`addresses in this device's code: ${kinds(pc.localDescription)}`, `addresses in the other device's code: ${kinds(pc.remoteDescription)}`);
      lines.push('local addresses:', ...of('local-candidate').map((r) => `  ${addr(r)}`));
      lines.push('remote addresses:', ...of('remote-candidate').map((r) => `  ${addr(r)}`));
      lines.push(
        'checks:',
        ...of('candidate-pair').map(
          (r) =>
            `  ${addr(byId.get(r.localCandidateId))} → ${addr(byId.get(r.remoteCandidateId))}: ${r.state}${r.nominated ? ' nominated' : ''}, requests sent ${r.requestsSent ?? '?'} received ${r.requestsReceived ?? '?'}, responses received ${r.responsesReceived ?? '?'}`,
        ),
      );
    } catch {
      lines.push('(connection statistics unavailable)');
    }
    return lines.join('\n');
  }

  async createOffer(): Promise<Uint8Array> {
    this.isOfferer = true;
    this.role = 'shows code (two-code pairing)';
    await this.pc.setLocalDescription(await this.pc.createOffer());
    return encodeSignal({ type: 'offer', ...(await this.localSignal('offer')) });
  }

  /** An offer only the remembered device peerId can answer, without sending a reply. */
  async createReconnectOffer(peerId: string): Promise<Uint8Array> {
    const pair = (await getPairs())[peerId];
    if (!pair) throw new Error('This device no longer remembers that one. Choose “Pair a new device” to pair again.');
    this.expectPeer = peerId;
    this.role = 'shows code (one-scan reconnect)';
    await this.pc.setLocalDescription(await this.pc.createOffer());
    const parts = await this.localSignal('reconnect');
    // The reply the other device will make, built here: its remembered certificate, credentials
    // from the shared secret, and no candidates (its address is learned from its first check).
    const creds = await reconnectCredentials(pair.secret, parts.ufrag, parts.pwd);
    await this.pc.setRemoteDescription({ type: 'answer', sdp: buildSdp({ ...creds, fingerprint: pair.fp, candidates: [], mid: parts.mid }, 'answer', { moreCandidates: true }) });
    this.holdOpen(parts.mid);
    return encodeSignal({ type: 'reconnect', ...parts, from: await deviceId(), to: peerId });
  }

  /**
   * Keep ICE waiting until the remembered device scans the code. With no address for the other
   * side, Firefox declares ICE failed after 5 seconds, long before anyone has scanned. A
   * placeholder candidate in TEST-NET-1 (192.0.2.0/24, reserved, never answers) keeps a check in
   * progress; a fresh one every 3 s replaces each as it times out.
   */
  private holdOpen(mid: string) {
    let n = 0;
    const add = () => {
      const s = this.pc.iceConnectionState;
      if (this.finished || this.opened || s === 'connected' || s === 'completed' || n >= 120) return;
      n++;
      this.pc.addIceCandidate({ candidate: `candidate:${9000 + n} 1 udp 1 192.0.2.1 ${20000 + n} typ host`, sdpMid: mid, sdpMLineIndex: 0 }).catch(noop);
      this.holdTimer = setTimeout(add, 3000);
    };
    add();
  }

  /** Returns the reply code to show, or null for a reconnect (no reply needed). */
  async acceptOffer(bytes: Uint8Array): Promise<Uint8Array | null> {
    const sig = decodeSignal(bytes);
    if (sig.type === 'reconnect') return this.acceptReconnect(sig);
    if (sig.type !== 'offer') throw new Error('That’s a reply code. On this device, choose “Show code” instead.');
    this.role = 'scans code (two-code pairing)';
    await this.pc.setRemoteDescription({ type: 'offer', sdp: buildSdp(sig, 'offer') });
    await this.pc.setLocalDescription(await this.pc.createAnswer());
    return encodeSignal({ type: 'answer', ...(await this.localSignal('answer')) });
  }

  private async acceptReconnect(sig: Extract<Signal, { type: 'reconnect' }>): Promise<null> {
    if (sig.to !== (await deviceId())) throw new Error('This code is for a different device. On the other device, choose “Pair a new device” to pair with this one.');
    const pair = (await getPairs())[sig.from];
    if (!pair) throw new Error('This device doesn’t remember the other one. On the other device, choose “Pair a new device” to pair again.');
    if (!this.persisted || certFingerprint(this.cert) !== pair.myFp) throw new Error('This device’s pairing has expired. On the other device, choose “Pair a new device” to pair again.');
    this.expectPeer = sig.from;
    this.role = 'scans code (one-scan reconnect)';
    await this.pc.setRemoteDescription({ type: 'offer', sdp: buildSdp(sig, 'offer') });
    const answer = await this.pc.createAnswer();
    const creds = await reconnectCredentials(pair.secret, sig.ufrag, sig.pwd);
    const sdp = answer.sdp!.replace(/^a=ice-ufrag:.*/gm, `a=ice-ufrag:${creds.ufrag}`).replace(/^a=ice-pwd:.*/gm, `a=ice-pwd:${creds.pwd}`);
    await this.pc.setLocalDescription({ type: 'answer', sdp });
    if (!this.pc.localDescription!.sdp.includes(`a=ice-ufrag:${creds.ufrag}`))
      throw new Error('This browser can’t connect from a single scan. On the other device, choose “Pair a new device” to connect with a code from each device.');
    return null;
  }

  /**
   * The fallback when this device scanned a reconnect code but can't reach the other device (it
   * can't look up a .local name): a short code with this device's own addresses, for the other
   * device to scan or paste (see addRemoteAddresses).
   */
  async addressCode(): Promise<Uint8Array> {
    await waitForIce(this.pc);
    const { candidates } = parseSdp(this.pc.localDescription!.sdp);
    if (!candidates.length) throw new Error('This browser didn’t share a local network address. Check that you’re connected to Wi-Fi, then try again.');
    this.note(`address code ready (${candidates.map((c) => (MDNS.test(c.addr) ? '.local name' : 'IP')).join(', ')})`);
    return encodeSignal({ type: 'addresses', from: await deviceId(), to: this.expectPeer!, candidates });
  }

  /** On the device showing a reconnect code: connect to the addresses the other device sent. */
  async addRemoteAddresses(bytes: Uint8Array) {
    const sig = decodeSignal(bytes);
    if (sig.type !== 'addresses') throw new Error('That isn’t the code the other device showed back.');
    if (sig.from !== this.expectPeer || sig.to !== (await deviceId())) throw new Error('That code is from a different device or an earlier attempt.');
    const mid = parseSdp(this.pc.localDescription!.sdp).mid;
    for (const [i, c] of sig.candidates.entries()) {
      await this.pc.addIceCandidate({ candidate: `candidate:${8000 + i} 1 udp ${2122260223 - i} ${c.addr} ${c.port} typ host`, sdpMid: mid, sdpMLineIndex: 0 });
    }
    this.note(`added ${sig.candidates.length} address(es) from the other device`);
  }

  async acceptAnswer(bytes: Uint8Array) {
    const sig = decodeSignal(bytes);
    if (sig.type !== 'answer') throw new Error('That’s the code this device is showing. Scan the reply from the other device.');
    // The reply doesn't carry the mid; it's always this device's own offer's.
    await this.pc.setRemoteDescription({ type: 'answer', sdp: buildSdp({ ...sig, mid: parseSdp(this.pc.localDescription!.sdp).mid }, 'answer') });
  }

  close() {
    this.finished = true;
    clearTimeout(this.holdTimer);
    try {
      this.ch.close();
      this.pc.close();
    } catch {
      /* ignore */
    }
  }

  private fail(e: SyncError) {
    if (this.finished) return;
    this.note(`error: ${e.message}`);
    // Tell the other device why, so it doesn't only see "disconnected".
    try {
      if (this.ch.readyState === 'open' && !e.fromPeer) this.ch.send(`!${e.message}`);
    } catch {
      /* already gone */
    }
    this.finished = true;
    // Read the statistics before closing, which discards them.
    void this.details().then((d) => {
      e.details = d;
      setTimeout(() => this.close(), 300);
      this.cb.onError(e);
    });
  }

  // Framing: a text frame "#<byteLength>", then that many bytes in binary frames.
  // Each message is [4-byte JSON length][JSON][optional binary payload].
  private async send(obj: Message, bin: ArrayBuffer | null = null) {
    const json = enc.encode(JSON.stringify(obj));
    const extra = bin ? new Uint8Array(bin) : new Uint8Array(0);
    const packet = new Uint8Array(4 + json.length + extra.length);
    new DataView(packet.buffer).setUint32(0, json.length);
    packet.set(json, 4);
    packet.set(extra, 4 + json.length);
    this.ch.send(`#${packet.length}`);
    for (let o = 0; o < packet.length; o += CHUNK) {
      if (this.ch.bufferedAmount > 1024 * 1024) await new Promise((r) => this.ch.addEventListener('bufferedamountlow', r, { once: true }));
      if (this.ch.readyState !== 'open') throw new Error('The other device disconnected.');
      this.ch.send(packet.subarray(o, Math.min(o + CHUNK, packet.length)));
    }
  }

  private onFrame(data: string | ArrayBuffer) {
    if (typeof data === 'string') {
      if (data[0] === '#') this.incoming = { size: Number(data.slice(1)), parts: [], got: 0 };
      if (data[0] === '!') {
        const e: SyncError = new Error(`The other device stopped the sync: ${data.slice(1)}`);
        e.fromPeer = true;
        this.fail(e);
      }
      return;
    }
    const inc = this.incoming;
    if (!inc) return;
    const part = new Uint8Array(data);
    inc.parts.push(part);
    inc.got += part.length;
    if (inc.got < inc.size) return;
    this.incoming = null;
    const packet = new Uint8Array(inc.size);
    let o = 0;
    for (const p of inc.parts) {
      packet.set(p, o);
      o += p.length;
    }
    const jlen = new DataView(packet.buffer).getUint32(0);
    const msg = JSON.parse(dec.decode(packet.subarray(4, 4 + jlen))) as Message;
    const bin = packet.subarray(4 + jlen);
    // Handle messages one at a time, in order.
    if (msg.t !== 'file') this.note(`received ${msg.t}`);
    this.queue = this.queue.then(() => this.onMessage(msg, bin)).catch((e) => this.fail(e));
  }

  private async start() {
    this.cb.onConnected();
    this.cb.onProgress('Connected. Comparing trees…');
    this.myFp = this.persisted ? certFingerprint(this.cert) : null;
    this.nonce = toHex(crypto.getRandomValues(new Uint8Array(32)));
    this.local = await this.snapshot();
    await this.send({
      t: 'hello',
      app: APP,
      format: NATIVE_VERSION,
      deviceId: await deviceId(),
      device: deviceName(),
      trees: this.local.trees,
      deletedTrees: this.local.deletedTrees,
      settings: this.local.settings,
      fileKeys: await db.listFileKeys(),
      fp: this.myFp,
      canAnswer: await canEditIceCredentials(),
      nonce: this.nonce,
    });
  }

  /** The person agreed (on this device): go ahead on both. */
  async confirm() {
    if (this.confirmed || !this.remote) return;
    await this.send({ t: 'confirm' });
    await this.go();
  }

  /** The person said no (on this device): stop on both without changing anything. */
  async cancel() {
    if (this.finished) return;
    try {
      await this.send({ t: 'cancel' });
    } catch {
      /* already gone */
    }
    this.finished = true;
    setTimeout(() => this.close(), 300);
  }

  private async snapshot() {
    return { trees: await allTrees(), deletedTrees: await deletedTrees(), settings: syncedSettings(getState().settings) };
  }

  private plan(): TreesPlan {
    return planSync(this.local!.trees, this.local!.deletedTrees, this.remote!.trees, this.remote!.deletedTrees);
  }

  private async onMessage(msg: Message, bin: Uint8Array) {
    switch (msg.t) {
      case 'hello': {
        if (msg.app !== APP) throw new Error('The other device isn’t running Relationships.');
        if (msg.format !== NATIVE_VERSION) throw new Error('The two devices are running different versions of the app. Reload the page on both, then try again.');
        if (this.expectPeer && msg.deviceId !== this.expectPeer) throw new Error('Connected to an unexpected device. Try again.');
        this.remote = msg;
        await this.remember(msg);
        // Both devices can work out both directions, so each shows the same preview.
        const mine = this.local!;
        // Untimed settings stay as each device has them, so each side's merge is worked out on its own.
        const theirs = syncedSettings(msg.settings ?? {});
        const here = describePlan(this.plan(), mine.trees, describeSettings(mine.settings, mergeSettings(mine.settings, theirs), themeName));
        const there = describePlan(planSync(msg.trees, msg.deletedTrees, mine.trees, mine.deletedTrees), msg.trees, describeSettings(theirs, mergeSettings(theirs, mine.settings), themeName));
        // Nothing to change on either side: no need to ask.
        if (here.empty && there.empty) await this.go();
        else {
          this.note('showing preview');
          this.cb.onPreview({ here, there, device: msg.device });
        }
        break;
      }
      case 'confirm':
        this.cb.onConfirmed();
        await this.go();
        break;
      case 'cancel':
        this.finished = true;
        this.close();
        this.cb.onCanceled();
        break;
      case 'want': {
        let n = 0;
        for (const key of msg.keys) {
          const b = await db.getFile(key);
          if (b) await this.send({ t: 'file', key, mime: b.type }, await b.arrayBuffer());
          this.cb.onProgress(`Sending media files ${++n} of ${msg.keys.length}…`);
        }
        await this.send({ t: 'files-done' });
        break;
      }
      case 'file':
        if (!this.wanted.has(msg.key)) return;
        // Saved straight away rather than held in memory; if the sync stops part way, the next
        // one won't ask for these again.
        await db.putFiles(new Map([[msg.key, new Blob([bin.slice()], { type: msg.mime })]]));
        this.cb.onProgress(`Receiving media files ${++this.received} of ${this.fileTotal}…`);
        break;
      case 'files-done':
        this.remoteFilesDone = true;
        await this.maybeApply();
        break;
      case 'applied':
        this.remoteApplied = true;
        this.maybeFinish();
        break;
    }
  }

  /** Remember the other device for one-scan reconnects (both devices store the same secret). */
  private async remember(msg: Hello) {
    const pairs = await getPairs();
    if (msg.fp && this.myFp && msg.nonce) {
      pairs[msg.deviceId] = { name: msg.device, fp: msg.fp, myFp: this.myFp, secret: await pairSecret(this.nonce, msg.nonce), canAnswer: msg.canAnswer !== false };
    } else delete pairs[msg.deviceId];
    await db.setKv('syncPairs', pairs);
  }

  private async go() {
    if (this.confirmed) return;
    this.confirmed = true;
    this.note('confirmed');
    this.cb.onProgress('Syncing…');
    const plan = this.plan();
    const have = new Set(await db.listFileKeys());
    const remoteHas = new Set(this.remote!.fileKeys);
    const gone = new Set([...plan.del, ...plan.put].map((t) => t.id));
    const final = [...this.local!.trees.filter((t) => !gone.has(t.id)), ...plan.put];
    for (const key of filesUsed(final)) if (!have.has(key) && remoteHas.has(key)) this.wanted.add(key);
    this.fileTotal = this.wanted.size;
    await this.send({ t: 'want', keys: [...this.wanted] });
    await this.maybeApply();
  }

  private async maybeApply() {
    if (this.applied || !this.confirmed || !this.remoteFilesDone) return;
    this.applied = true;
    this.cb.onProgress('Saving…');
    // Plan again against the trees as they are now, in case something changed meanwhile.
    this.local = await this.snapshot();
    const plan = this.plan();
    const settings = mergeSettings(this.local.settings, syncedSettings(this.remote!.settings ?? {}));
    // Counted as the preview shows them: a tree whose merge only settles edit times isn't "changed".
    const { trees } = describePlan(plan, this.local.trees);
    this.summary = {
      added: trees.filter((t) => t.kind === 'added').length,
      changed: trees.filter((t) => t.kind === 'changed').length,
      removed: trees.filter((t) => t.kind === 'removed').length,
      files: this.received,
      settings: describeSettings(this.local.settings, settings, themeName),
      device: this.remote!.device,
    };
    await applySyncPlan(plan);
    adoptSyncedSettings(settings);
    const peers = await getPeers();
    await db.setKv('syncPeers', { ...peers, [this.remote!.deviceId]: { name: this.remote!.device, at: new Date().toISOString() } });
    void requestPersistence();
    await this.send({ t: 'applied' });
    this.maybeFinish();
  }

  private maybeFinish() {
    if (!this.applied || !this.remoteApplied || this.finished) return;
    this.finished = true;
    // Give the last frame time to leave before closing.
    setTimeout(() => this.close(), 500);
    this.cb.onDone(this.summary!);
  }
}
